<template>
  <div
    v-if="shouldShowBanner"
    :class="['app-banner-component', { mastery: masteryApp, 'mobile-only': appBanner && appBanner.mobileWebOnly }]"
  >
    <template v-if="bannerType === 'app-banner' && shouldShowAppBanner">
      <banner
        :type="`app`"
        :variant="appBanner.bannerType && appBanner.bannerType.toLowerCase()"
        @banner-clicked="handleAppBannerClick"
      >
        <template #banner-text>
          {{ appBanner.bannerText }}
        </template>
        <template #banner-action>
          {{ appBanner.actionText }}
        </template>
      </banner>
      <div
        v-if="appBanner.dismissable"
        class="dismiss-button-container"
      >
        <button
          class="dismiss-button"
          @click="dismissBanner"
        >
          <icon
            name="cross-thin"
            variant="white"
          />
        </button>
      </div>
    </template>
    <component
      :is="bannerType"
      v-else
    />
  </div>
</template>

<script>
import StripeBanner from '@components/banners/stripe-banner';
import TrialBanner from '@components/banners/trial-banner';
import Banner from '@components/ui/banner';
import Icon from '@components/singletons/icon';
import { recordBannerDismissed, getDismissedBanners } from '@/utils/banners';

import TrialMixin from '@/mixins/trial';

export default {
  components: { Banner, Icon, StripeBanner, TrialBanner },
  mixins: [TrialMixin],
  props: {
    masteryApp: {
      type: Boolean,
      default: false,
    },
  },
    data() {
      return {
        dismissedBanners: null,
        loaded: false,
      };
    },
  computed: {
    appBanner() {
      const banners = this.$store.getters['app/appBanners'];
      return banners.find(b => b.active);
    },
    bannerType() {
      if (this.showStripeBanner) {
        return 'stripe-banner';
      } else if (this.showTrialBanner) {
        return 'trial-banner';
      } else if (this.showAppBanner) {
        return 'app-banner';
      }
    },
    hasPaid() {
      return this.$store.getters['session/hasPaid'];
    },
    shouldShowAppBanner() {
      if (this.appBanner && this.appBanner.userType === 'paid' && this.hasPaid) {
        return true;
      } else if (this.appBanner && this.appBanner.userType === 'free' && !this.hasPaid) {
        return true;
      } else if (this.appBanner && this.appBanner.userType === 'everyone') {
        return true;
      }
      return false;
    },
    shouldShowBanner() {
      if (!this.loaded) {
        return false;
      }
      if (!this.bannerType) {
        return false;
      }
      if (this.bannerType === 'app-banner' && !this.shouldShowAppBanner) {
        return false;
      }
      return true;
    },
    showAppBanner() {
      if (this.appBanner) {
        return !this.dismissedBanners.includes(this.appBanner.id);
      }
    },
    showStripeBanner() {
      const prs = this.$store.getters['purchaseReceipts/all'];
      const activePastDuePR = prs.find(pr => pr.pastDue && pr.active);
      if (activePastDuePR) {
        return true;
      }
    },
    showTrialBanner() {
      const isPaid = this.$store.getters['session/hasPaid'];
      if (!isPaid) {
        if (this.trialPurchaseReceipt) {
          if (this.trialPurchaseReceiptExpired) {
            // This comes from a product var is a string so we need to check it explicitly
            if (this.showExpiredTrialBanner === 'true') {
              return true;
            };
          } else {
            return true;
          }
        }
      }
    },
  },
  mounted() {
    const dismissedBanners = getDismissedBanners(this.$store);
    this.dismissedBanners = dismissedBanners;
    this.loaded = true;
  },
  methods: {
    dismissBanner() {
      if (this.appBanner) {
        this.dismissedBanners.push(this.appBanner.id);
        recordBannerDismissed(this.$store, this.appBanner.id);
      }
    },
    handleAppBannerClick() {
      if (this.appBanner?.actionUrl) {
        window.open(this.appBanner.actionUrl, '_blank');
      }
    },
  },
};
</script>

<style lang="scss">
.app-banner-component {
  // fullscreen banner height
  height: rem-calc(58);
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 900;

  &.mobile-only {
    display: none;
  }

  &:not(.mobile-only) {
    @media (min-width: 40em) {
      &~.topbar-component-container {
        // fullscreen banner height
        top: rem-calc(58) !important;
      }

      &~.router-view {
        // 48 (base height) + 58 (fullscreen banner height) = 106
        height: calc(100% - #{rem-calc(106)}) !important;
      }

      &~.sidebar-component-container,
      &~.router-view,
      &~.router-view .page-header-component-container {
        // 48 (base height) + 58 (fullscreen banner height) = 106
        top: rem-calc(106) !important;
      }

      &.mastery {
        position: fixed;
        z-index: 100;

        &~.mastery-sidebar-component {
          top: rem-calc(106) !important;
        }
        &~.router-view {
          padding-top: rem-calc(106) !important;
        }
      }
    }
  }

  @include breakpoint(small) {
    &.mobile-only {
      display: block;
    }

    // condensed banner height
    height: rem-calc(80);

    &~.topbar-component-container {
      // condensed banner height
      top: rem-calc(80) !important;
    }

    &~.router-view {
      // 72 (base height) + 80 (condensed banner height) = 152
      height: calc(100% - #{rem-calc(152)}) !important;
    }

    &~.sidebar-component-container,
    &~.router-view,
    &~.router-view .page-header-component-container {
      // 72 (base height) + 80 (condensed banner height) = 152
      top: rem-calc(152) !important;
    }

    &.mastery {
      &~.mastery-sidebar-component {
        top: initial !important;
      }
      &~.router-view {
        padding-top: rem-calc(152) !important;
      }
    }
  }

  .dismiss-button-container {
    position: absolute;
    top: 8px;
    right: 12px;

    .dismiss-button {
      padding: 4px;
      background: transparent;

      img {
        height: 12px;
        width: 12px;
      }
    }
  }
}
</style>
